import { Link } from '@tanstack/react-router';
import { ListChecks, MoreHorizontal, PauseCircle, Undo2, X } from 'lucide-react';
import { useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  BOARD_COLUMN_CARD_LIMIT,
  MANAGER_RESPONSE_LABELS,
  REVIEW_STATUS_LABELS,
  compareReviewQueue,
  profileBreakdownOf,
  reasonsForStages,
  type JobDetail,
  type RankedBy,
  type RankingRow,
  type ReviewStatus,
} from '@nest-recruit/shared';
import { Chip } from '../../../components/Chip';
import { SearchInput } from '../../../components/Input';
import { MenuItem, Popover } from '../../../components/Popover';
import { Segmented } from '../../../components/Segmented';
import { Skeleton, SkeletonLines } from '../../../components/Skeleton';
import { Stars } from '../../../components/Stars';
import { toneClass } from '../../../components/Tone';
import { MANAGER_RESPONSE_TONES, REVIEW_STATUS_TONES, STAGE_TONES } from '../../../components/status-tones';
import { EmptyState, ServerErrorState } from '../../../components/system-states/SystemState';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { formatNumber } from '../../../lib/format';
import { useModule } from '../../../lib/modules';
import { useMediaQuery } from '../../../lib/use-media-query';
import { useCanEdit } from '../../../stores/session';
import { useCvBankAccess } from '../../cv-bank/hooks/use-cv-bank-actions';
import { useJobOut } from '../../jobs/api/jobs-api';
import { usePipelineOptions } from '../../pipeline/api/pipeline-api';
import { BoardColumn, type DropState } from '../../pipeline/components/board/BoardColumn';
import { validatePipelineSearch, type PipelineSearch } from '../../pipeline/pipeline-search';
import { useRanking } from '../../ranking/api/ranking-api';
import { useProfileRanks } from '../../ranking/components/icp/use-profile-ranks';
import { WhyThisScore } from '../../ranking/components/WhyThisScore';
import { ReasonPicker } from '../../sorting/components/ReasonPicker';
import { useBringBack, useRejectWithReason, useReviewMove } from '../api/shortlist-api';
import { useReviewLayout } from '../hooks/use-review-layout';
import { rejectedLaneOf, type RejectedCard } from '../rejected-lane';
import { ReviewList } from './ReviewList';
import { ReviewPreview, previewOfRejected, previewOfRow, type ReviewDecision } from './ReviewPreview';
import styles from './ReviewStage.module.css';

// R6 — stage ④ Review on JOB-12: "Start review" into the review card (SCR-01), and the conveyor
// (SCR-03, PRD RV-5): To review → Shortlisted, and Rejected, with Maybe set aside. Drawn from
// RNK-01's own rows, so the lanes and the ranking always agree. A drag is a review decision with
// the review card's rules: Rejected asks for the reason, a sent card stays put (§L25).

type Lane = ReviewStatus | 'rejected';

const LANES: readonly ReviewStatus[] = ['to_review', 'shortlisted'];
/** A press becomes a drag after this many pixels — below it, it is a click (S16's board). */
const DRAG_THRESHOLD_PX = 6;
const CLICK_AFTER_DRAG_MS = 250;
/** Wide enough for the preview beside the lanes (Idea B); narrower, a card opens the full view. */
const PREVIEW_MEDIA = '(min-width: 1181px)';

/** What a drag carries: a card in a review lane, or someone in Rejected being brought back (F23.18). */
type Carried = { readonly kind: 'row'; readonly row: RankingRow } | { readonly kind: 'rejected'; readonly card: RejectedCard };

/** F23.18's refusal, in the server's words, for someone rejected at a later stage. */
const REJECTED_AFTER_REVIEW = "They were rejected after review, so they can't be brought back here.";

interface Drag {
  readonly item: Carried;
  readonly x: number;
  readonly y: number;
  readonly over: Lane | null;
}

interface ReviewStageProps {
  /**
   * Which job it is, and the two facts the profile's salary and location lines are judged against. D6: its Requested by and Line
   * manager, so the job's hiring manager opens the list on HR's picks.
   */
  readonly detail: Pick<JobDetail, 'id' | 'workMode' | 'salaryBudget'> & Partial<Pick<JobDetail, 'requestedByUserId' | 'lineManagerUserId'>>;
  readonly search: PipelineSearch;
  readonly onSearch: (next: PipelineSearch) => void;
}

/** D6 (ADR 0080): ④ Review opens as the list; the board is the other layout, and each person's choice is kept. */
export function ReviewStage({ detail, search, onSearch }: ReviewStageProps): ReactNode {
  const ranking = useRanking(detail.id);
  const [layout, setLayout] = useReviewLayout();
  if (ranking.isPending) return layout === 'list' ? <SkeletonLines count={8} /> : <BoardSkeleton />;
  if (ranking.error || !ranking.data) return <ServerErrorState onRetry={() => void ranking.refetch()} />;
  const switcher = (
    <Segmented
      label="Show review as"
      value={layout}
      options={[
        { value: 'list', label: 'List' },
        { value: 'board', label: 'Board' },
      ]}
      onChange={setLayout}
    />
  );
  if (layout === 'list') return <ReviewList detail={detail} stored={ranking.data.rows} rankedBy={ranking.data.rankedBy} search={search} onSearch={onSearch} switcher={switcher} />;
  return <Conveyor detail={detail} stored={ranking.data.rows} rankedBy={ranking.data.rankedBy} search={search} onSearch={onSearch} switcher={switcher} />;
}

function Conveyor({
  detail,
  stored,
  rankedBy,
  search,
  onSearch,
  switcher,
}: { readonly detail: ReviewStageProps['detail']; readonly stored: readonly RankingRow[]; readonly rankedBy: RankedBy; readonly switcher: ReactNode } & Omit<ReviewStageProps, 'detail'>): ReactNode {
  const jobId = detail.id;
  // ADR 0062 §2.7: the lanes are ordered by the same ranks Ranking shows, worked out here from the profile's ratings, so a Focus-bar
  // change is in the lanes at once (Review's queue on the server follows a few seconds later).
  const { rankedRows: rows, items: profileItems, ranks: profileRanks } = useProfileRanks({ jobId, job: detail, rows: stored, rankedBy });
  const canDecide = useCanEdit('screening_decision');
  const out = useJobOut(jobId, 1, 'rejected', true);
  const { move, pending } = useReviewMove(jobId);
  const rejection = useRejectWithReason(jobId);
  const bringBack = useBringBack(jobId);
  const options = usePipelineOptions(canDecide);
  const isPoolsOn = useModule('pools');
  const mayPool = useCanEdit('talent_pool');
  const canPool = isPoolsOn && mayPool;
  // The review card's rule too (Group 3 Q2): the tick only for someone who may add to the CV Bank.
  const mayAddToCvBank = useCvBankAccess().canEdit;
  const [drag, setDrag] = useState<Drag | null>(null);
  const [picking, setPicking] = useState<{ readonly row: RankingRow; readonly anchor: AnchorRect } | null>(null);
  const [menu, setMenu] = useState<{ readonly row: RankingRow; readonly anchor: AnchorRect } | null>(null);
  const [rejectedMenu, setRejectedMenu] = useState<{ readonly card: RejectedCard; readonly anchor: AnchorRect } | null>(null);
  const [collapsed, setCollapsed] = useState<ReadonlySet<Lane>>(new Set());
  const [fullViewId, setFullViewId] = useState<string | null>(null);
  const hasPreview = useMediaQuery(PREVIEW_MEDIA);
  const query = (search.q ?? '').trim().toLowerCase();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const draggedAt = useRef(0);

  // The search narrows every lane to the people whose name or city matches, so one person is
  // findable among a thousand (owner, 2026-10-25). The order within each lane is unchanged.
  const shownRows = useMemo(() => (query === '' ? rows : rows.filter((row) => matchesQuery([row.name, row.city], query))), [rows, query]);
  const lanes = useMemo(() => lanesOf(shownRows), [shownRows]);
  const allLanes = useMemo(() => lanesOf(rows), [rows]);
  const waiting = allLanes.to_review.length + allLanes.maybe.length;
  const rejected = useMemo(() => {
    const lane = rejectedLaneOf(rows, out.data);
    if (query === '') return lane;
    const cards = lane.cards.filter((card) => matchesQuery([card.name], query));
    return { ...lane, cards, count: cards.length };
  }, [rows, out.data, query]);
  const matchCount = lanes.to_review.length + lanes.shortlisted.length + lanes.maybe.length + rejected.count;
  // The preview shows the person picked on the board, else the next one waiting, so it is never empty.
  const laneRows = [...lanes.to_review, ...lanes.maybe, ...lanes.shortlisted];
  // Someone picked in Rejected shows too (F23.18), with the ways back into review.
  const previewedRejected = rejected.cards.find((card) => card.applicationId === search.app) ?? null;
  const previewed = previewedRejected ? null : (laneRows.find((row) => row.applicationId === search.app) ?? laneRows[0] ?? null);
  const rejectedShown = Math.min(rejected.cards.length, BOARD_COLUMN_CARD_LIMIT);
  const previewedRank = previewed ? profileRanks?.get(previewed.applicationId) : undefined;
  const previewedProfile = previewed && previewedRank ? profileBreakdownOf(profileItems, previewedRank.values, previewedRank, previewedRank.state, previewed.knockoutPenalty) : null;
  const reasons = useMemo(() => reasonsForStages(options.data?.reasons ?? [], ['screening']), [options.data?.reasons]);

  if (rows.length === 0) {
    return (
      <>
        <div className={styles.toolbar}>
          <span className={styles.switcher}>{switcher}</span>
        </div>
        <EmptyState
        title="Nobody to review yet"
        body="Import a spreadsheet of applicants on ② Import. Each one is ranked, then lands here To review."
        action={
          <Link to="/jobs/$jobId/$tab" params={{ jobId, tab: 'import' }} className={styles.action}>
            Import a CSV
          </Link>
        }
        isInset
      />
      </>
    );
  }

  function laneAt(x: number, y: number): { lane: Lane; element: HTMLElement } | null {
    for (const element of wrapRef.current?.querySelectorAll<HTMLElement>('[data-lane]') ?? []) {
      const rect = element.getBoundingClientRect();
      const lane = laneOf(element.dataset.lane);
      if (lane && x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) return { lane, element };
    }
    return null;
  }

  function drop(row: RankingRow, to: Lane, at: AnchorRect): void {
    const from = row.reviewStatus ?? 'to_review';
    if (to === from) return;
    if (refusalOf(row) !== null) return;
    if (to === 'rejected') {
      setPicking({ row, anchor: at });
      return;
    }
    void move([{ applicationId: row.applicationId, name: row.name, from }], to);
  }

  /** F23.18: a rejected person dropped on a review lane comes back into it. */
  function bringBackTo(card: RejectedCard, to: Lane): void {
    if (to === 'rejected' || !card.isFromReview) return;
    void bringBack.bringBack(card, to);
  }

  function dropCarried(item: Carried, to: Lane, at: AnchorRect): void {
    if (item.kind === 'row') drop(item.row, to, at);
    else bringBackTo(item.card, to);
  }

  /** S16's hand-rolled pointer drag (Tomo's `BoardView.tsx`), on the review lanes. */
  function onDragStart(event: ReactPointerEvent<HTMLElement>, item: Carried): void {
    if (!canDecide || event.button !== 0) return;
    if (event.target instanceof Element && event.target.closest('a,button')) return;
    const startX = event.clientX;
    const startY = event.clientY;
    let isStarted = false;
    let over: { lane: Lane; element: HTMLElement } | null = null;
    const onMove = (pointer: PointerEvent): void => {
      if (!isStarted && Math.abs(pointer.clientX - startX) + Math.abs(pointer.clientY - startY) < DRAG_THRESHOLD_PX) return;
      isStarted = true;
      over = laneAt(pointer.clientX, pointer.clientY);
      setDrag({ item, x: pointer.clientX, y: pointer.clientY, over: over?.lane ?? null });
    };
    const finish = (): void => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('keydown', onKey, true);
      setDrag(null);
    };
    const onUp = (): void => {
      finish();
      if (!isStarted) return;
      draggedAt.current = Date.now();
      if (over) dropCarried(item, over.lane, anchorFrom(over.element));
    };
    // Esc puts the card back where it was, and nothing is sent.
    const onKey = (key: KeyboardEvent): void => {
      if (key.key !== 'Escape') return;
      key.stopPropagation();
      isStarted = false;
      finish();
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('keydown', onKey, true);
  }

  /** A lane's empty line; while searching it says there is nobody matching instead. */
  function emptyText(text: string): string {
    return query === '' ? text : 'No matches.';
  }

  function dropFor(lane: Lane): DropState {
    if (!drag) return { kind: 'idle' };
    const isOver = drag.over === lane;
    if (drag.item.kind === 'rejected') {
      if (lane === 'rejected') return { kind: 'idle' };
      if (!drag.item.card.isFromReview) return { kind: 'no', isOver, reason: REJECTED_AFTER_REVIEW };
      return { kind: 'ok', isOver, hint: 'Drop to bring them back into review.' };
    }
    if (lane === (drag.item.row.reviewStatus ?? 'to_review')) return { kind: 'idle' };
    const refusal = refusalOf(drag.item.row);
    if (refusal) return { kind: 'no', isOver, reason: refusal };
    return { kind: 'ok', isOver, hint: lane === 'rejected' ? 'Drop to reject — you will pick the reason.' : null };
  }

  function open(applicationId: string): void {
    if (isJustAfter(draggedAt.current)) return;
    onSearch(validatePipelineSearch({ ...search, app: applicationId }));
  }

  function toggle(lane: Lane): void {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(lane)) next.delete(lane);
      else next.add(lane);
      return next;
    });
  }

  function select(row: RankingRow | undefined): void {
    onSearch(validatePipelineSearch({ ...search, app: row?.applicationId }));
  }

  /** Who the preview moves to once this person is decided: the next one down their lane. */
  function nextAfter(row: RankingRow): RankingRow | undefined {
    const lane = lanes[row.reviewStatus ?? 'to_review'];
    return lane[lane.findIndex((other) => other.applicationId === row.applicationId) + 1];
  }

  function decideFromPreview(row: RankingRow, to: ReviewDecision, trigger: HTMLElement): void {
    if (to === 'rejected') {
      setPicking({ row, anchor: anchorFrom(trigger) });
      return;
    }
    const next = nextAfter(row);
    drop(row, to, anchorFrom(trigger));
    select(next);
  }

  function cardsOf(lane: ReviewStatus): ReactNode {
    return lanes[lane].slice(0, BOARD_COLUMN_CARD_LIMIT).map((row) => (
      <ReviewCard
        key={row.applicationId}
        row={row}
        isPending={pending.has(row.applicationId) || rejection.pending.has(row.applicationId)}
        isDragging={drag?.item.kind === 'row' && drag.item.row.applicationId === row.applicationId}
        isSelected={hasPreview && previewed?.applicationId === row.applicationId}
        onDragStart={(event) => onDragStart(event, { kind: 'row', row })}
        onOpen={() => open(row.applicationId)}
        onMenu={canDecide ? (trigger) => setMenu({ row, anchor: anchorFrom(trigger) }) : null}
      />
    ));
  }

  function footerOf(lane: ReviewStatus): ReactNode {
    const more = lanes[lane].length - BOARD_COLUMN_CARD_LIMIT;
    if (more <= 0) return undefined;
    return (
      <Link to="/jobs/$jobId/$tab" params={{ jobId, tab: 'ranking' }} className={styles.more}>
        {formatNumber(more)} more — see everyone on ③ Ranking
      </Link>
    );
  }

  return (
    <div className={styles.stage}>
      {/* "Start review" is the job header's (SortingEntry), on Ranking and Review (DECISIONS-LOG 2026-10-19 Q7). */}
      <div className={styles.toolbar}>
        <span className={styles.search}>
          <SearchInput
            aria-label="Search this review by name or city"
            placeholder="Search by name or city"
            value={search.q ?? ''}
            onChange={(event) => onSearch(validatePipelineSearch({ ...search, q: event.target.value === '' ? undefined : event.target.value }))}
            onClear={() => onSearch(validatePipelineSearch({ ...search, q: undefined }))}
            // `/` focuses the primary search on any list screen (06-qol §2).
            data-primary-search
          />
        </span>
        {query !== '' ? (
          <span className={styles.clear}>{matchCount === 0 ? `Nobody matches “${search.q?.trim() ?? ''}”.` : `${formatNumber(matchCount)} ${matchCount === 1 ? 'match' : 'matches'}.`}</span>
        ) : (
          <span className={styles.clear}>{waiting === 0 ? 'Everyone has been reviewed.' : `${formatNumber(waiting)} waiting for review, best fit first.`}</span>
        )}
        <span className={styles.switcher}>{switcher}</span>
        <p className={styles.hint}>
          {hasPreview ? 'Click a card to preview it on the right. A shortlist · H maybe · R reject, or drag a card.' : 'Start review opens the review card: A shortlist · H maybe · R reject. Or drag a card to move it.'}
        </p>
      </div>

      <div className={hasPreview ? styles.workspace : undefined}>
        <div ref={wrapRef} className={[styles.board, drag ? styles.isDragging : ''].filter(Boolean).join(' ')} role="list" aria-label="Review conveyor">
          {LANES.map((lane) => (
            <BoardColumn
              key={lane}
              lane={lane}
              tone={REVIEW_STATUS_TONES[lane]}
              label={REVIEW_STATUS_LABELS[lane]}
              count={lanes[lane].length}
              shown={Math.min(lanes[lane].length, BOARD_COLUMN_CARD_LIMIT)}
              isCollapsed={collapsed.has(lane)}
              onToggle={() => toggle(lane)}
              drop={dropFor(lane)}
              empty={emptyText(lane === 'to_review' ? 'Nobody waiting.' : 'Shortlisted people land here, then go to ⑤ Shortlist.')}
              footer={footerOf(lane)}
              isFluid={hasPreview}
            >
              {cardsOf(lane)}
            </BoardColumn>
          ))}
          <BoardColumn
            lane="rejected"
            tone={STAGE_TONES.rejected}
            label="Rejected"
            count={rejected.count}
            shown={rejectedShown}
            isCollapsed={collapsed.has('rejected')}
            onToggle={() => toggle('rejected')}
            drop={dropFor('rejected')}
            empty={emptyText('Drop someone here to reject them. You pick the reason; they never see it.')}
            isFluid={hasPreview}
            footer={rejected.count > rejectedShown ? <span className={styles.more}>{formatNumber(rejected.count - rejectedShown)} more — see them under Out on the stage bar</span> : undefined}
          >
            {rejected.cards.slice(0, rejectedShown).map((card) => (
              <RejectedCardView
                key={card.applicationId}
                card={card}
                isPending={bringBack.pending.has(card.applicationId)}
                isDragging={drag?.item.kind === 'rejected' && drag.item.card.applicationId === card.applicationId}
                isSelected={hasPreview && previewedRejected?.applicationId === card.applicationId}
                canMove={canDecide && card.isFromReview}
                onDragStart={(event) => onDragStart(event, { kind: 'rejected', card })}
                onOpen={() => open(card.applicationId)}
                onMenu={(trigger) => setRejectedMenu({ card, anchor: anchorFrom(trigger) })}
              />
            ))}
          </BoardColumn>
          <div className={styles.aside}>
            <BoardColumn
              lane="maybe"
              tone={REVIEW_STATUS_TONES.maybe}
              label="Maybe, set aside"
              count={lanes.maybe.length}
              shown={Math.min(lanes.maybe.length, BOARD_COLUMN_CARD_LIMIT)}
              isCollapsed={collapsed.has('maybe')}
              onToggle={() => toggle('maybe')}
              drop={dropFor('maybe')}
              empty={emptyText('Maybe comes back round once To review is empty.')}
              footer={footerOf('maybe')}
              isFluid={hasPreview}
            >
              {cardsOf('maybe')}
            </BoardColumn>
          </div>
        </div>
        {hasPreview && previewedRejected && (
          <ReviewPreview
            key={previewedRejected.applicationId}
            person={previewOfRejected(previewedRejected)}
            decide={
              canDecide
                ? {
                    refusal: previewedRejected.isFromReview ? null : REJECTED_AFTER_REVIEW,
                    isPending: bringBack.pending.has(previewedRejected.applicationId),
                    onDecide: (to) => bringBackTo(previewedRejected, to),
                  }
                : null
            }
            onOpenFull={() => setFullViewId(previewedRejected.applicationId)}
          />
        )}
        {hasPreview && previewed && (
          <ReviewPreview
            key={previewed.applicationId}
            person={previewOfRow(previewed, previewedProfile)}
            decide={
              canDecide
                ? {
                    refusal: refusalOf(previewed),
                    isPending: pending.has(previewed.applicationId) || rejection.pending.has(previewed.applicationId),
                    onDecide: (to, trigger) => decideFromPreview(previewed, to, trigger),
                  }
                : null
            }
            onOpenFull={() => setFullViewId(previewed.applicationId)}
          />
        )}
      </div>

      {drag &&
        createPortal(
          <div className={styles.ghost} style={{ left: drag.x, top: drag.y }} aria-hidden>
            {drag.item.kind === 'row' ? drag.item.row.name : drag.item.card.name}
          </div>,
          document.body,
        )}

      <Popover anchor={menu?.anchor ?? null} onClose={() => setMenu(null)} label="Move" align="right">
        {menu && (
          <MoveMenu
            row={menu.row}
            onMove={(to) => {
              setMenu(null);
              drop(menu.row, to, menu.anchor);
            }}
          />
        )}
      </Popover>

      <Popover anchor={rejectedMenu?.anchor ?? null} onClose={() => setRejectedMenu(null)} label="Bring back" align="right">
        {rejectedMenu && (
          <BringBackMenu
            onMove={(to) => {
              setRejectedMenu(null);
              bringBackTo(rejectedMenu.card, to);
            }}
          />
        )}
      </Popover>

      <Popover anchor={picking?.anchor ?? null} onClose={() => setPicking(null)} label="Reject" align="left">
        {picking && (
          <ReasonPicker
            key={picking.row.applicationId}
            mode="reject"
            reasons={reasons}
            pools={options.data?.pools ?? []}
            canPool={canPool}
            contactAttempts={picking.row.contactAttempts}
            candidateName={picking.row.name}
            canAddToCvBank={mayAddToCvBank && picking.row.cv !== 'none'}
            onConfirm={(choice) => {
              const person = { applicationId: picking.row.applicationId, name: picking.row.name };
              if (hasPreview && picking.row.applicationId === previewed?.applicationId) select(nextAfter(picking.row));
              setPicking(null);
              void rejection.reject([person], choice.reasonCode, { poolIds: choice.poolIds, newPoolNames: choice.newPoolNames, addToCvBank: choice.addToCvBank });
            }}
          />
        )}
      </Popover>

      {/* Beside the preview, the full view opens from its button; without it, a card opens it. */}
      {hasPreview ? (
        <WhyThisScore applicationId={fullViewId} jobId={jobId} onClose={() => setFullViewId(null)} />
      ) : (
        <WhyThisScore applicationId={search.app ?? null} jobId={jobId} onClose={() => onSearch(validatePipelineSearch({ ...search, app: undefined }))} />
      )}
    </div>
  );
}

function ReviewCard({
  row,
  isPending,
  isDragging,
  isSelected,
  onDragStart,
  onOpen,
  onMenu,
}: {
  readonly row: RankingRow;
  readonly isPending: boolean;
  readonly isDragging: boolean;
  readonly isSelected: boolean;
  readonly onDragStart: (event: ReactPointerEvent<HTMLElement>, row: RankingRow) => void;
  readonly onOpen: (row: RankingRow) => void;
  readonly onMenu: ((trigger: HTMLElement) => void) | null;
}): ReactNode {
  const status = row.reviewStatus ?? 'to_review';
  const isSent = isSentOf(row);
  return (
    <article
      className={[toneClass, styles.card, isDragging ? styles.dragging : '', isPending ? styles.pending : '', isSelected ? styles.selected : ''].filter(Boolean).join(' ')}
      aria-current={isSelected || undefined}
      data-tone={REVIEW_STATUS_TONES[status]}
      tabIndex={0}
      aria-label={`${row.name}, match ${row.score ?? 'not ranked'}`}
      onPointerDown={(event) => onDragStart(event, row)}
      onKeyDown={(event) => {
        if (event.target === event.currentTarget && event.key === 'Enter') onOpen(row);
      }}
    >
      <header className={styles.head}>
        <button type="button" className={styles.name} onClick={() => onOpen(row)}>
          {row.name}
        </button>
        {/* The ⋯ menu is every move a drag makes, from the keyboard (06-qol §2); it shows on hover and focus. */}
        {onMenu && !isSent && (
          <button type="button" className={styles.menu} aria-label={`Move ${row.name}`} onClick={(event) => onMenu(event.currentTarget)}>
            <MoreHorizontal size={16} aria-hidden />
          </button>
        )}
        <span className={styles.score} title="Match with the ideal candidate">
          {row.score ?? '—'}
        </span>
      </header>
      <div className={styles.meta}>
        <Stars value={row.stars} />
        {row.hrStars !== null && <span className={styles.hr}>HR ★ {row.hrStars.toFixed(1)}</span>}
        {isPending && <span className={styles.saving}>Saving…</span>}
      </div>
      {(row.missingMustHaves.length > 0 || isSent) && (
        <div className={styles.chips}>
          {row.missingMustHaves.length > 0 && (
            <Chip tone="rejected" plain>
              Missing: {row.missingMustHaves.join(', ')}
            </Chip>
          )}
          {isSent && row.managerResponse && (
            <Chip tone={MANAGER_RESPONSE_TONES[row.managerResponse]} plain>
              {MANAGER_RESPONSE_LABELS[row.managerResponse]}
            </Chip>
          )}
        </div>
      )}
    </article>
  );
}

/**
 * A rejected person. Clicking shows them in the preview; someone rejected in review can be carried
 * back to a lane, or moved with ⋯, while the job is open (F23.18).
 */
function RejectedCardView({
  card,
  isPending,
  isDragging,
  isSelected,
  canMove,
  onDragStart,
  onOpen,
  onMenu,
}: {
  readonly card: RejectedCard;
  readonly isPending: boolean;
  readonly isDragging: boolean;
  readonly isSelected: boolean;
  readonly canMove: boolean;
  readonly onDragStart: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onOpen: () => void;
  readonly onMenu: (trigger: HTMLElement) => void;
}): ReactNode {
  return (
    <article
      className={[toneClass, styles.card, canMove ? '' : styles.settled, isDragging ? styles.dragging : '', isPending ? styles.pending : '', isSelected ? styles.selected : ''].filter(Boolean).join(' ')}
      aria-current={isSelected || undefined}
      data-tone={STAGE_TONES.rejected}
      tabIndex={0}
      aria-label={`${card.name}, rejected`}
      onPointerDown={canMove ? onDragStart : undefined}
      onKeyDown={(event) => {
        if (event.target === event.currentTarget && event.key === 'Enter') onOpen();
      }}
    >
      <header className={styles.head}>
        <button type="button" className={styles.name} onClick={onOpen}>
          {card.name || '—'}
        </button>
        {canMove && (
          <button type="button" className={styles.menu} aria-label={`Bring ${card.name} back`} onClick={(event) => onMenu(event.currentTarget)}>
            <MoreHorizontal size={16} aria-hidden />
          </button>
        )}
        {(card.isSaving || isPending) && <span className={styles.saving}>Saving…</span>}
      </header>
      {card.reasonLabel && (
        <div className={styles.chips}>
          <Chip tone="rejected" plain>
            {card.reasonLabel}
          </Chip>
        </div>
      )}
    </article>
  );
}

/** F23.18's ⋯ menu on someone in Rejected: the ways back into review, from the keyboard (06-qol §2). */
function BringBackMenu({ onMove }: { readonly onMove: (to: ReviewStatus) => void }): ReactNode {
  return (
    <div className={styles.menuList} role="menu">
      <MenuItem icon={<Undo2 size={14} aria-hidden />} onClick={() => onMove('to_review')}>
        Back to To review
      </MenuItem>
      <MenuItem icon={<PauseCircle size={14} aria-hidden />} onClick={() => onMove('maybe')}>
        Maybe
      </MenuItem>
      <MenuItem icon={<ListChecks size={14} aria-hidden />} onClick={() => onMove('shortlisted')}>
        Shortlist
      </MenuItem>
    </div>
  );
}

/** The ⋯ menu: every move a drag makes, from the keyboard (06-qol §2). */
function MoveMenu({ row, onMove }: { readonly row: RankingRow; readonly onMove: (to: Lane) => void }): ReactNode {
  const from = row.reviewStatus ?? 'to_review';
  return (
    <div className={styles.menuList} role="menu">
      {from !== 'shortlisted' && (
        <MenuItem icon={<ListChecks size={14} aria-hidden />} onClick={() => onMove('shortlisted')}>
          Shortlist
        </MenuItem>
      )}
      {from !== 'maybe' && (
        <MenuItem icon={<PauseCircle size={14} aria-hidden />} onClick={() => onMove('maybe')}>
          Maybe
        </MenuItem>
      )}
      {from !== 'to_review' && (
        <MenuItem icon={<Undo2 size={14} aria-hidden />} onClick={() => onMove('to_review')}>
          Back to To review
        </MenuItem>
      )}
      <MenuItem icon={<X size={14} aria-hidden />} isDanger onClick={() => onMove('rejected')}>
        Reject…
      </MenuItem>
    </div>
  );
}

function BoardSkeleton(): ReactNode {
  return (
    <div className={styles.board} aria-busy="true">
      {LANES.map((lane) => (
        <div key={lane} className={styles.skeletonLane}>
          <Skeleton shape="title" width="60%" />
          <Skeleton />
          <Skeleton />
        </div>
      ))}
    </div>
  );
}

/** Everyone still in `screening` (or at `new`), by review lane, best fit first (§L31). */
function lanesOf(rows: readonly RankingRow[]): Readonly<Record<ReviewStatus, readonly RankingRow[]>> {
  const lanes: Record<ReviewStatus, RankingRow[]> = { to_review: [], shortlisted: [], maybe: [] };
  for (const row of rows) {
    if (row.stage !== 'screening' && row.stage !== 'new') continue;
    lanes[row.reviewStatus ?? 'to_review'].push(row);
  }
  const order = (a: RankingRow, b: RankingRow): number =>
    compareReviewQueue({ id: a.applicationId, rankKey: a.rankKey, reviewStatus: a.reviewStatus, importOrder: a.order }, { id: b.applicationId, rankKey: b.rankKey, reviewStatus: b.reviewStatus, importOrder: b.order });
  return { to_review: lanes.to_review.sort(order), shortlisted: lanes.shortlisted.sort(order), maybe: lanes.maybe.sort(order) };
}

/** A click this soon after a drag ends is the drag's own pointer-up, not a click. */
function isJustAfter(draggedAt: number): boolean {
  return Date.now() - draggedAt < CLICK_AFTER_DRAG_MS;
}

/** Whether any of the values contains the (already lower-cased) query. */
function matchesQuery(values: readonly (string | null)[], query: string): boolean {
  return values.some((value) => value !== null && value.toLowerCase().includes(query));
}

function isSentOf(row: RankingRow): boolean {
  return row.reviewStatus === 'shortlisted' && row.managerResponse !== null && row.managerResponse !== 'not_sent';
}

/** Why this card can't land in that lane, in the words the server would use, or null (§L25). */
function refusalOf(row: RankingRow): string | null {
  if (row.stage !== 'screening') return 'They are still at New. Review them in the review card.';
  if (isSentOf(row)) return 'Already sent to the manager — answer it on ⑤ Shortlist.';
  return null;
}

function laneOf(value: string | undefined): Lane | null {
  if (value === 'to_review' || value === 'shortlisted' || value === 'maybe' || value === 'rejected') return value;
  return null;
}
